import {useCallback, useMemo} from 'react';
import {useAsync} from 'react-use';
import {RigsService} from '../api/generated/RigsService';
import {BasicPage} from '../components/basic-page';
import {BreadcrumbPart} from '../components/page-title';
import {ROUTES} from '../routes';
import {rigSchema, RigSchema} from './schemas';
import {createForm} from '../forms';
import {SubmitHandler} from 'react-hook-form';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {useNotification} from '../hooks/use-notifications';
import {Button, Group} from '@mantine/core';
import {AsyncStateContainer} from '../components/async-state-container';

const Form = createForm<RigSchema>();

export const RigAdd = () => {
  const navigate = useNavigate();
  const notifications = useNotification();
  const {partnerSiteId} = useParams<{partnerSiteId: string}>();
  const partnerSiteIdNum = Number(partnerSiteId);

  const breadcrumbs: BreadcrumbPart[] = useMemo(
    () => [
      {title: 'S/N Tracking', href: ROUTES.ADMIN.SERIAL_NUMBER_TRACKING.INDEX.path},
      {title: 'Tracked Serial Numbers', href: ROUTES.ADMIN.SERIAL_NUMBER_TRACKING.DETAIL.buildPath({id: partnerSiteIdNum})},
      {title: 'Rigs', href: ROUTES.ADMIN.RIGS.INDEX.buildPath({partnerSiteId: partnerSiteIdNum})},
      {title: 'Create'},
    ],
    [partnerSiteIdNum],
  );

  const defaultValues: Partial<RigSchema> = useMemo(
    () => ({
      name: '',
      notificationEmails: '',
      partnerSiteId: partnerSiteIdNum,
    }),
    [partnerSiteIdNum],
  );

  const onSubmit = useCallback<SubmitHandler<RigSchema>>(
    async (value) => {
      const response = await RigsService.create({body: {...value, partnerSiteId: partnerSiteIdNum}});

      if (response.hasErrors) {
        return response;
      }

      notifications.success('Rig Created');
      navigate(ROUTES.ADMIN.RIGS.INDEX.buildPath({partnerSiteId: partnerSiteIdNum}));
    },
    [navigate, notifications, partnerSiteIdNum],
  );

  const listingPath = ROUTES.ADMIN.RIGS.INDEX.buildPath({partnerSiteId: partnerSiteIdNum});

  return (
    <BasicPage title={breadcrumbs}>
      <Form schema={rigSchema} onSubmit={onSubmit} defaultValues={defaultValues}>
        <Form.Container>
          <FormFields />
          <Group mt={24} justify='flex-end'>
            <Button type='button' variant='outline' component={Link} to={listingPath}>
              Cancel
            </Button>
            <Form.Submit>Create Rig</Form.Submit>
          </Group>
        </Form.Container>
      </Form>
    </BasicPage>
  );
};

export const RigEdit = () => {
  const navigate = useNavigate();
  const notifications = useNotification();
  const {partnerSiteId, id} = useParams<{partnerSiteId: string; id: string}>();
  const partnerSiteIdNum = Number(partnerSiteId);
  const rigId = Number(id);

  const fetchRig = useAsync(async () => {
    const {data} = await RigsService.getById({id: rigId});
    return data;
  }, [rigId]);

  const rig = fetchRig.value || undefined;
  const rigName = rig?.name ?? '';

  const breadcrumbs = useMemo<BreadcrumbPart[]>(
    () => [
      {title: 'S/N Tracking', href: ROUTES.ADMIN.SERIAL_NUMBER_TRACKING.INDEX.path},
      {title: 'Tracked Serial Numbers', href: ROUTES.ADMIN.SERIAL_NUMBER_TRACKING.DETAIL.buildPath({id: partnerSiteIdNum})},
      {title: 'Rigs', href: ROUTES.ADMIN.RIGS.INDEX.buildPath({partnerSiteId: partnerSiteIdNum})},
      {title: rigName},
    ],
    [partnerSiteIdNum, rigName],
  );

  const onSubmit = useCallback<SubmitHandler<RigSchema>>(
    async (value) => {
      const response = await RigsService.update({id: rigId, body: {...value, partnerSiteId: partnerSiteIdNum}});

      if (response.hasErrors) {
        return response;
      }

      notifications.success('Rig Updated');
      navigate(ROUTES.ADMIN.RIGS.INDEX.buildPath({partnerSiteId: partnerSiteIdNum}));
    },
    [rigId, navigate, notifications, partnerSiteIdNum],
  );

  const listingPath = ROUTES.ADMIN.RIGS.INDEX.buildPath({partnerSiteId: partnerSiteIdNum});

  return (
    <BasicPage title={breadcrumbs}>
      <AsyncStateContainer {...fetchRig}>
        <Form schema={rigSchema} onSubmit={onSubmit} defaultValues={rig}>
          <Form.Container>
            <FormFields />
            <Group mt={24} justify='flex-end'>
              <Button type='button' variant='outline' component={Link} to={listingPath}>
                Cancel
              </Button>
              <Form.Submit>Update Rig</Form.Submit>
            </Group>
          </Form.Container>
        </Form>
      </AsyncStateContainer>
    </BasicPage>
  );
};

const FormFields = () => {
  return (
    <>
      <Form.Row>
        <Form.TextInput name='name' label='Name' />
      </Form.Row>
      <Form.Row mt={12}>
        <Form.TextArea
          name='notificationEmails'
          label='Notification Emails'
          description='Comma-separated list of email addresses to receive monthly rig reports'
          autosize
          minRows={2}
        />
      </Form.Row>
    </>
  );
};
